import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  Users,
  GraduationCap,
  FileBarChart,
  Receipt,
  // AlertCircle, // 暫時隱藏：事件 / 教練爭議分布
  // KanbanSquare, // 暫時隱藏：學員 CRM 看板
  Building2,
  // Moon, // 暫時隱藏：學員 CRM 沉睡
  // TrendingUp, // 暫時隱藏：招生漏斗
  // Star, // 暫時隱藏：介紹人排行
  // Heart, // 暫時隱藏：教練 CRM「我的介紹」
} from "lucide-react";
import { Logo } from "@/components/ui/Logo";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  end?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

type SidebarRole = "owner" | "manager" | "trainer";

function getGroups(role: SidebarRole): NavGroup[] {
  if (role === "trainer") {
    return [
      {
        title: "學員",
        items: [
          {
            label: "我的學員",
            to: "/trainer/students",
            icon: <Users className="h-4 w-4" />,
          },
        ],
      },
      // CRM（介紹人）— 暫時隱藏整組：現在沒要做 CRM
      // {
      //   title: "我的介紹",
      //   items: [
      //     {
      //       label: "我介紹的 Lead",
      //       to: "/trainer/my-referrals",
      //       icon: <Heart className="h-4 w-4" />,
      //     },
      //   ],
      // },
    ];
  }

  const prefix = `/${role}`;
  // owner / manager — 共享 base + CRM + 事件 + analytics 分節
  const groups: NavGroup[] = [
    {
      title: "概覽",
      items: [
        { label: "首頁", to: `${prefix}/dashboard`, icon: <LayoutDashboard className="h-4 w-4" /> },
        { label: "排課", to: `${prefix}/schedule`, icon: <Calendar className="h-4 w-4" /> },
      ],
    },
    {
      title: "成員",
      items: [
        { label: "教練", to: `${prefix}/coaches`, icon: <GraduationCap className="h-4 w-4" /> },
        { label: "學員", to: `${prefix}/students`, icon: <Users className="h-4 w-4" /> },
      ],
    },
    // M2 (feature/3) — 學員 CRM（暫時隱藏整組）
    // {
    //   title: "學員 CRM",
    //   items: [
    //     {
    //       label: "看板",
    //       to: `${prefix}/leads`,
    //       icon: <KanbanSquare className="h-4 w-4" />,
    //       end: true,
    //     },
    //     { label: "沉睡", to: `${prefix}/leads/sleeping`, icon: <Moon className="h-4 w-4" /> },
    //   ],
    // },
    // M4 (feature/4) — 客訴 / 事件（暫時隱藏整組）
    // {
    //   title: "事件",
    //   items: [
    //     {
    //       label: "客訴 / 事件",
    //       to: `${prefix}/incidents`,
    //       icon: <AlertCircle className="h-4 w-4" />,
    //     },
    //     ...(role === "owner"
    //       ? [
    //           {
    //             label: "待審 drafts",
    //             to: `${prefix}/incidents/drafts`,
    //             icon: <AlertCircle className="h-4 w-4" />,
    //           },
    //         ]
    //       : []),
    //   ],
    // },
  ];

  // 分析 — combines M1 reports baseline + M2 招生漏斗 / 介紹人排行 + M4 教練爭議分布
  const analyticsItems: NavItem[] = [
    { label: "報表", to: `${prefix}/reports`, icon: <FileBarChart className="h-4 w-4" /> },
    // 暫時隱藏：招生漏斗 / 教練爭議分布
    // {
    //   label: "招生漏斗",
    //   to: `${prefix}/reports/recruitment-funnel`,
    //   icon: <TrendingUp className="h-4 w-4" />,
    // },
    // {
    //   label: "教練爭議分布",
    //   to: `${prefix}/reports/coach-disputes`,
    //   icon: <AlertCircle className="h-4 w-4" />,
    // },
  ];
  // 暫時隱藏：介紹人排行（Owner-only）
  // if (role === "owner") {
  //   analyticsItems.splice(2, 0, {
  //     label: "介紹人排行",
  //     to: `${prefix}/reports/referrers`,
  //     icon: <Star className="h-4 w-4" />,
  //   });
  // }
  groups.push({ title: "分析", items: analyticsItems });

  // M1 對帳台 — owner only (feature/2)
  if (role === "owner") {
    groups.push({
      title: "結算",
      items: [
        { label: "對帳台", to: `${prefix}/settlements`, icon: <Receipt className="h-4 w-4" /> },
      ],
    });
  }

  // 設定 — owner only（場館管理：新增 / 重新命名場館）
  if (role === "owner") {
    groups.push({
      title: "設定",
      items: [
        {
          label: "場館管理",
          to: `${prefix}/settings/branches`,
          icon: <Building2 className="h-4 w-4" />,
        },
      ],
    });
  }

  return groups;
}

export function Sidebar({ role }: { role: SidebarRole }) {
  const groups = getGroups(role);

  return (
    <aside className="border-border bg-surface flex h-full w-60 shrink-0 flex-col border-r">
      <div className="flex h-12 items-center px-5">
        <Logo size="sm" showText />
      </div>
      <nav className="scrollbar-thin flex-1 overflow-y-auto px-3 py-2" aria-label="主選單">
        {groups.map((group) => (
          <div key={group.title} className="mb-4">
            <p className="text-ink-400 px-2 pt-2 pb-1 text-[11px] font-semibold tracking-wider uppercase">
              {group.title}
            </p>
            <ul role="list" className="flex flex-col gap-px">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex h-9 items-center gap-2.5 rounded-md px-2.5 text-sm font-medium transition-colors",
                        "focus-visible:ring-primary-500 focus-visible:ring-2 focus-visible:outline-none",
                        isActive
                          ? "bg-primary-50 text-primary-700"
                          : "text-ink-700 hover:bg-ink-50 hover:text-ink-900",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span
                            aria-hidden
                            className="bg-primary-500 absolute inset-y-1.5 left-0 w-0.5 rounded-full"
                          />
                        )}
                        <span
                          className={cn(
                            "shrink-0",
                            isActive ? "text-primary-600" : "text-ink-400 group-hover:text-ink-600",
                          )}
                          aria-hidden
                        >
                          {item.icon}
                        </span>
                        {item.label}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-border text-ink-400 border-t px-5 py-3 text-[11px]">
        v0.1 · Demo 模式
      </div>
    </aside>
  );
}
